"use client";

import { memo, useState, useMemo, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";

// Компоненты
import BaseChartBlock from "../shared/BaseChartBlock";
import { SuspiciousAlert } from "../ui/SuspiciousAlert";
import { MinutesChart, QuestionChart } from "./components";

// Типы и интерфейсы
import type { AnswersReportData } from "@/types/endpoints/reports/GetAiReportById";
import type { AnswersChartBlockProps } from "../types";
import type { DisplayMode } from "./types";
import type { AssignmentAttemptEntity, StudentResultEntity } from "@/types/canonical";

// Хуки и API
import { useReportData } from "../../../../hooks/useReportData";
import { useGetAssignmentAttemptDetailQuery } from "@/api/assignment/assignmentApi";
import { useSuspiciousMinute } from "../hooks/useSuspiciousMinute";

// Утилиты
import {
  parseResultsChart,
  parseAssignmentReportData,
  buildDetailedQuestionsSeries,
  buildQuestionSeriesFromResults
} from "./utils";

// Константы
import { NORMAL_THRESHOLD } from "../constants";

interface QDensityChartBlockProps extends AnswersChartBlockProps {
  assignmentId?: number;
  contextId?: number;
  onAnalyticsData?: (data: any) => void;
  questionResults?: StudentResultEntity[];
  entity?: AssignmentAttemptEntity;
  filteredComponents?: any[];
  showFullAnalysisButton?: boolean;
  onOpenFullAnalysis?: () => void;
}

export const QDensityChartBlock = memo(function QDensityChartBlock({
  attemptId,
  assignmentReportData,
  assignmentId,
  contextId,
  onAnalyticsData,
  questionResults,
  entity,
  filteredComponents,
  showFullAnalysisButton = false,
  onOpenFullAnalysis,
}: QDensityChartBlockProps) {
  const t = useTranslations();
  const [displayMode, setDisplayMode] = useState<DisplayMode>("minutes");
  const { id } = useParams();
  
  const finalAssignmentId = assignmentId ?? Number(id);
  
  const { data: qDensityData } = useReportData(attemptId, "q_density");

  const { data: assignmentAttemptData } = useGetAssignmentAttemptDetailQuery(
    {
      assignmentId: finalAssignmentId,
      contextId: contextId!,
      attemptId: attemptId,
    },
    { skip: displayMode === "minutes" || !contextId }
  );

  useEffect(() => {
    if (qDensityData && onAnalyticsData) {
      onAnalyticsData(qDensityData);
    }
  }, [qDensityData, onAnalyticsData]);

  return (
    <BaseChartBlock
      attemptId={attemptId}
      reportType="q_density"
      titleKey="label-answers-report"
      showModeToggle={true}
      displayMode={displayMode}
      onDisplayModeChange={setDisplayMode}
      showFullAnalysisButton={showFullAnalysisButton}
      onOpenFullAnalysis={onOpenFullAnalysis}
    >
      {(report) => (
        <QDensityChartContent
          report={report as unknown as AnswersReportData}
          displayMode={displayMode}
          assignmentReportData={assignmentReportData}
          filteredComponents={filteredComponents}
          entity={entity}
          questionResults={questionResults}
          attemptId={attemptId}
          assignmentAttemptData={assignmentAttemptData}
        />
      )}
    </BaseChartBlock>
  );
});

interface QDensityChartContentProps {
  report: AnswersReportData;
  displayMode: DisplayMode;
  assignmentReportData?: any;
  filteredComponents?: any[];
  entity?: AssignmentAttemptEntity;
  questionResults?: StudentResultEntity[];
  attemptId: number;
  assignmentAttemptData?: any;
}

function QDensityChartContent({
  report,
  displayMode,
  assignmentReportData,
  filteredComponents,
  entity,
  questionResults,
  attemptId,
  assignmentAttemptData,
}: QDensityChartContentProps) {
  const t = useTranslations();

  // ВАЖНО: хуки нельзя вызывать внутри render-prop колбэка.
  // Вынесли всю логику в отдельный компонент, чтобы порядок хуков был стабильным.

  const chartData = useMemo(() => {
    if (displayMode !== "minutes") return [];

    const aiReportData = parseResultsChart(report.graph_data, NORMAL_THRESHOLD);

    if (aiReportData.length > 0) {
      return aiReportData;
    }

    if (assignmentReportData?.results_chart) {
      return parseAssignmentReportData(assignmentReportData.results_chart, NORMAL_THRESHOLD);
    }

    return [];
  }, [report.graph_data, displayMode, assignmentReportData]);

  const questionsChartData = useMemo(() => {
    if (displayMode === "questions") {
      // Используем переданные отфильтрованные данные из ResultsTab если они есть
      if (filteredComponents && questionResults) {
        return buildQuestionSeriesFromResults(filteredComponents, questionResults, attemptId);
      }
      // Используем переданные данные из ResultsTab если они есть
      if (entity?.quiz_components && questionResults) {
        return buildQuestionSeriesFromResults(entity.quiz_components, questionResults, attemptId);
      }
      // Fallback к старой логике
      if (assignmentAttemptData?.quiz_components) {
        return buildDetailedQuestionsSeries(assignmentAttemptData.quiz_components);
      }
    }
    return [];
  }, [displayMode, filteredComponents, entity, questionResults, attemptId, assignmentAttemptData]);

  const suspiciousMinute = useSuspiciousMinute(chartData);

  // В режиме "questions" используем временной порог (30 секунд на ответ)
  const currentThreshold = displayMode === "questions" ? 30 : NORMAL_THRESHOLD;

  return (
    <>
      {suspiciousMinute && (
        <SuspiciousAlert
          variant="warning"
          message={t("label-suspicious-activity-at-minute", { minute: suspiciousMinute })}
        />
      )}

      {displayMode === "minutes" ? (
        <MinutesChart
          data={chartData}
          normalThreshold={currentThreshold}
          height={150}
          displayMode={displayMode}
        />
      ) : (
        <QuestionChart
          data={questionsChartData}
          height={150}
        />
      )}
    </>
  );
}
